import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'

import {
  HomeTabComponentDetailType,
  HomeTabComponentFormValuesType,
} from 'features/MultiCategory/@types/homeTabComponent'
import {
  HOME_TAB_COMPONENT_SEGMENTATION_TYPE_ID,
  HOME_TAB_TARGETING_TYPE,
} from 'features/MultiCategory/constant/homeTabCreateEdit'
import {
  createHomeTabComponent as createHomeTabComponentService,
  CreateHomeTabComponentPayloadType,
  deleteHomeTabComponent as deleteHomeTabComponentService,
  getHomeTabComponent,
  GetHomeTabComponentResponseType,
  sortHomeTabComponents,
  updateHomeTabComponent as updateHomeTabComponentService,
  UpdateHomeTabComponentPayloadType,
} from 'features/MultiCategory/services/homeTabComponent'
import { SLICE_NAME, setDetail, setIsLoading, setIsSubmitting } from './slice'

/** `additional_info` arrives as a JSON string or as the parsed value; keep a string. */
const toAdditionalInfo = (value: unknown) => {
  if (typeof value === 'string') {
    return value
  }

  return value ? JSON.stringify(value) : ''
}

const mapHomeTabComponent = (
  data: GetHomeTabComponentResponseType['data'],
): HomeTabComponentDetailType => ({
  id: data.homepage_tabbing_component_id,
  tabId: data.homepage_tabbing_id,
  typeId: data.homepage_tabbing_component_type_id,
  name: data.component_name || '',
  position: data.position,
  layout: data.layout || '',
  referenceIds: data.reference_ids || [],
  additionalInfo: toAdditionalInfo(data.additional_info),
  targetingTypeId: data.targeting_type_id,
  targetedUserIds: data.targeted_user_ids || [],
  startDateTime: data.start_date_time || '',
  endDateTime: data.end_date_time || '',
  isRecurringSchedule: Boolean(data.is_recurring_schedule),
  isActive: Boolean(data.is_active),
  locationIds: data.location_ids || [],
  targetedSegmentations: (data.targeted_segmentations || []).map((segmentation) => ({
    id: segmentation.id || 0,
    typeId: segmentation.type_id,
    segmentTypeId: segmentation.segment_type_id,
    segmentTypeName: segmentation.segment_type_name || '',
  })),
})

export type FetchHomeTabComponentParamsType = {
  tabId: number
  componentId: number
}

export const fetchHomeTabComponent = createAsyncThunk(
  `${SLICE_NAME}/fetchHomeTabComponent`,
  async ({ tabId, componentId }: FetchHomeTabComponentParamsType, { dispatch }) => {
    dispatch(setIsLoading(true))
    dispatch(setDetail(null))

    try {
      const {
        data: { data },
      } = await getHomeTabComponent(tabId, componentId)

      dispatch(setDetail(mapHomeTabComponent(data)))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

/** Fields both create and update take straight from the drawer. */
const toComponentPayloadBase = (values: HomeTabComponentFormValuesType) => {
  const targetingTypeId = values.targetingTypeId || HOME_TAB_TARGETING_TYPE.NORMAL_USER
  const isTargetedUser = targetingTypeId === HOME_TAB_TARGETING_TYPE.TARGETED_USER
  const isSegmentedUser = targetingTypeId === HOME_TAB_TARGETING_TYPE.SEGMENTED_USER

  return {
    component_name: values.name.trim(),
    reference_ids: values.referenceIds,
    /* Only Category Specific has one — the key stays off the payload otherwise. */
    ...(values.additionalInfo === undefined ? {} : { additional_info: values.additionalInfo }),
    targeting_type_id: targetingTypeId,
    targeted_user_ids: isTargetedUser ? values.targetedUserIds : [],
    ...(values.startDateTime ? { start_date_time: values.startDateTime } : {}),
    ...(values.endDateTime ? { end_date_time: values.endDateTime } : {}),
    is_recurring_schedule: values.isRecurringSchedule,
    location_ids: values.locationIds,
    targeted_segmentations: isSegmentedUser
      ? values.segmentIds.map((segmentId) => ({
          type_id: HOME_TAB_COMPONENT_SEGMENTATION_TYPE_ID,
          segment_type_id: segmentId,
        }))
      : [],
  }
}

export type CreateHomeTabComponentParamsType = {
  tabId: number
  typeId: number
  values: HomeTabComponentFormValuesType
}

export const createHomeTabComponent = createAsyncThunk(
  `${SLICE_NAME}/createHomeTabComponent`,
  async (
    { tabId, typeId, values }: CreateHomeTabComponentParamsType,
    { dispatch, rejectWithValue },
  ) => {
    dispatch(setIsSubmitting(true))

    try {
      const payload: CreateHomeTabComponentPayloadType = {
        ...toComponentPayloadBase(values),
        homepage_tabbing_id: tabId,
        homepage_tabbing_component_type_id: typeId,
        /* 0 lets the API append the component at the end of the tab. */
        position: 0,
        layout: values.layout,
        is_active: true,
      }

      const {
        data: { data },
      } = await createHomeTabComponentService(payload)

      toastSuccess('Komponen berhasil ditambahkan')

      return data.homepage_tabbing_component_id
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsSubmitting(false))
    }
  },
)

/**
 * Update keeps every field the API owns but does not surface in the drawer
 * (layout, position, active flag) by resending the stored value.
 */
const buildComponentPayload = (
  detail: HomeTabComponentDetailType,
  values: HomeTabComponentFormValuesType,
): UpdateHomeTabComponentPayloadType => ({
  ...toComponentPayloadBase(values),
  homepage_tabbing_component_id: detail.id,
  homepage_tabbing_id: detail.tabId,
  homepage_tabbing_component_type_id: detail.typeId,
  position: detail.position,
  layout: values.layout || detail.layout,
  is_active: detail.isActive,
})

export const updateHomeTabComponent = createAsyncThunk(
  `${SLICE_NAME}/updateHomeTabComponent`,
  async (values: HomeTabComponentFormValuesType, { dispatch, getState, rejectWithValue }) => {
    const {
      multiCategoryHomeTabComponent: { detail },
    } = getState() as StoreStateType

    if (!detail) {
      return rejectWithValue('Detail komponen belum termuat')
    }

    dispatch(setIsSubmitting(true))

    try {
      await updateHomeTabComponentService(buildComponentPayload(detail, values))

      toastSuccess('Komponen berhasil disimpan')

      return detail.id
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsSubmitting(false))
    }
  },
)

export type DeleteHomeTabComponentParamsType = {
  tabId: number
  componentId: number
}

export const deleteHomeTabComponent = createAsyncThunk(
  `${SLICE_NAME}/deleteHomeTabComponent`,
  async (
    { tabId, componentId }: DeleteHomeTabComponentParamsType,
    { dispatch, rejectWithValue },
  ) => {
    dispatch(setIsSubmitting(true))

    try {
      await deleteHomeTabComponentService({
        homepage_tabbing_id: tabId,
        homepage_tabbing_component_id: componentId,
      })

      toastSuccess('Komponen berhasil dihapus')

      return componentId
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsSubmitting(false))
    }
  },
)

export type UpdateHomeTabComponentPositionsParamsType = {
  tabId: number
  /** Component ids in their final order; positions come from the index. */
  orderedIds: number[]
}

/** Reorders a tab's components through the dedicated sort endpoint. */
export const updateHomeTabComponentPositions = createAsyncThunk(
  `${SLICE_NAME}/updateHomeTabComponentPositions`,
  async (
    { tabId, orderedIds }: UpdateHomeTabComponentPositionsParamsType,
    { dispatch, rejectWithValue },
  ) => {
    if (!orderedIds.length) {
      return []
    }

    dispatch(setIsSubmitting(true))

    try {
      await sortHomeTabComponents({
        homepage_tabbing_id: tabId,
        components: orderedIds.map((id, index) => ({ id, position: index + 1 })),
      })

      toastSuccess('Posisi komponen berhasil disimpan')

      return orderedIds
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsSubmitting(false))
    }
  },
)
